import { TheNightlySection } from '@news-mono/common'
import { 
    IconChevronUpTN, 
    AdUnitWrapperProps,
    metrics,
} from '@news-mono/component-library'
import {
    AdDefinition,
    AdTargeting,
    GetRouteAdTargeting,
    PageType,
    TogglesReduxState,
    Topic,
} from '@news-mono/web-common'
import {
    GalleryV4DTO,
    PageMetaDTO,
} from '@west-australian-newspapers/publication-types'
import { layout } from '../../../App.routing'
import { getNoIndexMeta } from './allowList'
import { toFeatureState } from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { sectionNavItems, subTopicNavItems } from '../../../navigation'
import { getLatestEditionSegment } from '../../home/get-editions-segment'
import React from 'react'

const getTopParentTopic = (topic: Topic): Topic => {
    if (topic.parent) {
        return getTopParentTopic(topic.parent)
    }

    return topic
}

export const createGalleryRoute = ({
    gallery,
    numberGalleryItems,
    getAdTargeting,
    pageMeta,
    section,
    toggles,
}: {
    gallery: GalleryV4DTO
    numberGalleryItems: number
    section: TheNightlySection
    getAdTargeting: GetRouteAdTargeting
    pageMeta?: PageMetaDTO
    toggles?: TogglesReduxState
}): PageType<TheNightlySection> => {
    const featureState = toggles && toFeatureState(toggles)

    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        'gallery',
        section,
        gallery.topics.primary,
        ...gallery.topics.secondary,
    )

    const adTargeting: AdTargeting = {
        pageId: gallery.slug,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    const numberOfGalleryAds = Math.ceil((numberGalleryItems - 1) / 3)

    // eslint-disable-next-line prefer-spread
    const adContent = Array.apply(null, Array(numberOfGalleryAds)).map(
        (_: any, i: number) => {
            const adInfo: AdUnitWrapperProps & { slot: AdDefinition } = {
                noticePosition: 'below-center',
                hiddenUntilLoaded: false,
                padding: [0, 0, 18, 0],
                slot: {
                    id: `gallery-${i}`,
                    size: 'leaderboard768Above',
                },
                adType: 'inline',
            }
            return adInfo
        },
    )

    return {
        kind: 'page',
        heading: gallery.heading,
        pageMeta: {
            ...pageMeta,
            description: gallery.homepageTeaser,
            meta: getNoIndexMeta(gallery.source, featureState),
        },
        socialMeta: {
            title: gallery.socialHead || gallery.homepageHead,
            description: gallery.socialTeaser || gallery.homepageTeaser,
        },
        hideHeading: true,
        noMetaTitleTemplate: false,
        pageType: 'publication',
        adTargeting,
        section,
        compositions: [
            layout.composition({
                type: 'box',
                props: {
                    containerWidth:
                        metrics.thenightly.siteMetrics.mainContentWidth,
                    verticalSpacing: 'unset',
                    horizontalGutters: 'mobSm',
                    verticalGutters: ['md', 'md'],
                },
                contentAreas: {
                    main: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                containerWidth:
                                    metrics.thenightly.siteMetrics.mainContentWidth,
                                horizontalGutters: 'mobSm',
                                hasBackgroundFill: true,
                                verticalSpacing: 'md',

                                breakpoints: {
                                    md: {
                                        horizontalGutters: 'md',
                                    },
                                },
                            },
                            contentAreas: {
                                main: [
                                    layout.component(
                                        {
                                            type: 'thenightly-breadcrumb',
                                            props: {
                                                topics: gallery.topics,
                                                nightlySectionNavItems:
                                                    sectionNavItems,
                                                subTopicNavItems:
                                                    subTopicNavItems,
                                            },
                                        },
                                    ),
                                    layout.component({
                                        type: 'gallery',
                                        props: {
                                            meta: {
                                                kind: 'gallery',
                                                slug: gallery.slug,
                                                numberImages:
                                                    numberGalleryItems,
                                                galleryAds: adContent,
                                            },
                                            section,
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
            layout.composition({
                type: 'box',
                props: {
                    verticalSpacing: 'outerMargin',
                    horizontalSpacing: 'outerMargin',
                    horizontalSpacingBreakpoint: 'md',
                },
                contentAreas: {
                    main: [
                        getLatestEditionSegment('article'),
                    ],
                },
            }),
            layout.composition({
                type: 'box',
                props: {
                    horizontalSpacing: 'outerMargin',
                    horizontalSpacingBreakpoint: 'sm',
                },
                contentAreas: {
                    main: [
                        layout.component({
                            type: 'tn-article-cards',
                            props: {
                                sectionHeading: 'Top Stories',
                                dataDefinitionArgs:
                                {
                                    type: 'curation',
                                    name: 'top-stories',
                                    offset: 0,
                                    pageSize: 8,
                                },
                            },
                        }),
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                horizontalSpacingBreakpoint:
                                    'sm',
                                horizontalSpacing:
                                    'outerMargin',
                                verticalGutters: ['lg', 'sm'],
                                flex: {
                                    default: {
                                        flexDirection:
                                            'column',
                                        gap: '40px',
                                    },
                                    lg: {
                                        gap: '32px',
                                    },
                                },
                            },
                            contentAreas: {
                                main: [
                                    layout.component({
                                        type: 'nightly-button',
                                        props: {
                                            variant: 'default',
                                            text: 'BACK TO TOP',
                                            color: 'primary',
                                            fill: 'text',
                                            layout: {
                                                alignSelf: 'center',
                                            },
                                            action: {
                                                type: 'button',
                                                onClick:
                                                    () =>
                                                        window.scrollTo(
                                                            {
                                                                top: 0,
                                                            },
                                                        ),
                                            },
                                            icon: {
                                                iconPosition: 'right',
                                                IconElement: (<IconChevronUpTN />),
                                            }
                                        },
                                    }),
                                ],
                            },
                        }),

                    ],
                },
            }),
        ],
    }
}